import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { HOME } from '../../../routes/paths';
import ListView from '../../components/views/listView';
import { MasterMessage } from '../../../masters/masterMessages';
import moment from 'moment';
import { Chip } from '@mui/material';
import ModalCaseClosure from './modalCaseClosure';
import {
    asyncGetCasesClosures,
    asyncDeleteErrorCases,
} from '../../../services/caseClosure.service';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid';
import DatePickerField from '../../components/general/fields/datePickerField';
import CancelIcon from '@mui/icons-material/Cancel';
import { getGridStringOperators } from '@mui/x-data-grid';
import FieldSelect from '../../components/general/fields/fieldSelect';
import FieldText from '../../components/general/fields/fieldText';
import { ToastSwal } from '../../components/general/toastSwal/toastSwal';
import Swal from 'sweetalert2';

export enum CaseClosureEnum {
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
    ERROR = 'ERROR',
    INPROG = 'INPROG',
}

const ListCaseClosure = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<any>();
    const [lengthRows, setLengthRows] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(20);
    const [open, setOpen] = useState<boolean>(false);
    const [saveLoad, setSaveLoad] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [caseId, setCaseId] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [deleting, setDeleting] = useState<boolean>(false);
    const [forceReload, setForceReload] = useState<boolean>(false);
    const filterOperators = getGridStringOperators().filter(({ value }) =>
        ['equals'].includes(value)
    );
    const { titleList } = MasterMessage.caseClosure;

    useEffect(() => {
        const dateStart = startDate
            ? dayjs(startDate)
                .startOf('day')
                .add(5, 'h')
                .format('YYYY/MM/DD HH:mm:ss')
            : null;
        const dateEnd = endDate
            ? dayjs(endDate)
                .endOf('day')
                .add(5, 'h')
                .format('YYYY/MM/DD HH:mm:ss')
            : null;

        if (saveLoad && page !== 0) {
            setPage(0);
            setSaveLoad(false);
            return;
        } else if (saveLoad) setSaveLoad(false);

        if (loading || saveLoad || forceReload) {
            asyncGetCasesClosures(
                page,
                pageSize,
                dateStart,
                dateEnd,
                caseId,
                status
            )
                .then((body: any) => {
                    if (body.items !== undefined) {
                        setRows(body.items.items);
                        setLengthRows(body.items.length);
                    }
                })
                .finally(() => {
                    setLoading(false);
                    setForceReload(false);
                });
        }
    }, [
        page,
        pageSize,
        saveLoad,
        startDate,
        endDate,
        caseId,
        status,
        forceReload,
    ]);

    const paginator = (paginationModel: any) => {
        if (paginationModel.page !== page) {
            setPage(paginationModel.page);
            setLoading(true);
        } else if (paginationModel.pageSize !== pageSize) {
            setPageSize(paginationModel.pageSize);
            setLoading(true);
        }
    };

    const columns = [
        {
            field: 'caseId',
            headerName: 'Número caso',
            minWidth: 120,
            flex: 1,
            filterOperators,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 1,
            type: 'date',
            filterable: false,
            valueFormatter: (params: any) =>
                params?.value !== null
                    ? moment(params?.value)
                        .subtract(5, 'h')
                        .format('DD/MM/YYYY HH:mm')
                    : null,
        },
        {
            field: 'closeDate',
            headerName: 'Fecha de cierre',
            minWidth: 120,
            flex: 1,
            type: 'date',
            filterable: false,
            renderCell: (row: any) => {
                return row?.row?.updated_at &&
                    row?.row?.status === CaseClosureEnum.CLOSED
                    ? moment(row?.row?.updated_at)
                        .subtract(5, 'h')
                        .format('DD/MM/YYYY HH:mm')
                    : null;
            },
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 100,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            filterOperators,
            renderCell: (row: any) => {
                if (row.row.status === CaseClosureEnum.OPEN) {
                    return (
                        <Chip
                            label="ABIERTO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="info"
                        />
                    );
                } else if (row.row.status === CaseClosureEnum.INPROG) {
                    return (
                        <Chip
                            label="EN PROGRESO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="info"
                        />
                    );
                } else if (row.row.status === CaseClosureEnum.CLOSED) {
                    return (
                        <Chip
                            label="CERRADO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="success"
                        />
                    );
                } else {
                    return (
                        <Chip
                            label="ERROR"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="error"
                        />
                    );
                }
            },
        },
        {
            field: 'createdBy',
            headerName: 'Creado Por',
            minWidth: 120,
            flex: 1,
            filterable: false,
            valueFormatter: (params: any) =>
                params?.value.name + ' ' + params?.value.surname || '',
        },
        {
            field: 'error',
            headerName: 'Error',
            minWidth: 120,
            flex: 2,
            filterable: false,
            renderCell: (params: any) => (
                <span title={params?.value || ''}>{params?.value || ''}</span>
            ),
        },
    ];

    const validateStartDate = (value: any) => {
        setLoading(true);
        setStartDate(value);
        if (value && startDate) {
            setEndDate(null);
        }
    };

    const validateEndDate = (value: any) => {
        setLoading(true);
        setEndDate(value);
    };

    const validateStatus = (value: any) => {
        setLoading(true);
        setStatus(value);
    };

    const validateCaseId = (value: any) => {
        setLoading(true);
        setCaseId(value);
    };

    const clearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setStatus('');
        setLoading(true);
    };

    const validateFilters = (filter: any) => {
        if (filter) {
            switch (filter.field) {
                case 'caseId':
                    return setCaseId(filter.value);
            }
        } else {
            setCaseId('');
        }
        setLoading(true);
    };

    const handleDeleteErrorCases = async () => {
        const dateStart = startDate
            ? dayjs(startDate)
                .startOf('day')
                .add(5, 'h')
                .format('YYYY/MM/DD HH:mm:ss')
            : '';
        const dateEnd = endDate
            ? dayjs(endDate)
                .endOf('day')
                .add(5, 'h')
                .format('YYYY/MM/DD HH:mm:ss')
            : '';

        // Confirmación antes de eliminar
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción eliminará permanentemente todos los casos ERROR que coincidan con los filtros aplicados. Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });

        if (result.isConfirmed) {
            try {
                setDeleting(true);

                const response = await asyncDeleteErrorCases(
                    dateStart,
                    dateEnd,
                    caseId
                );

                ToastSwal.fire({
                    icon: 'success',
                    title: 'Casos ERROR eliminados correctamente',
                    text:
                        (response as any)?.data?.message ||
                        `Se eliminaron ${
                            (response as any)?.data?.deletedCount || 0
                        } casos ERROR`,
                });

                // Recargar la lista
                setForceReload(true);
            } catch (error: any) {
                ToastSwal.fire({
                    icon: 'error',
                    title: 'Error al eliminar casos ERROR',
                    text:
                        error?.response?.data?.message ||
                        error?.message ||
                        'Ha ocurrido un error inesperado',
                });
            } finally {
                setDeleting(false);
            }
        }
    };

    const dataStatus = [
        {
            id: CaseClosureEnum.OPEN,
            name: 'Abierto',
        },
        {
            id: CaseClosureEnum.CLOSED,
            name: 'Cerrado',
        },
        {
            id: CaseClosureEnum.ERROR,
            name: 'Error',
        },
        {
            id: CaseClosureEnum.INPROG,
            name: 'En Progreso',
        },
    ];

    return (
        <>
            <ListView
                title={titleList}
                titleButtonAdd={'Importar'}
                startIconButtonAdd={<FileUploadIcon color="secondary" />}
                onClickAdd={() => setOpen(true)}
                onClickBack={() => navigate(HOME)}
                enabledButtonGeneral={Boolean(
                    startDate !== null || status !== ''
                )}
                titleButtonGeneral={'Limpiar Filtros'}
                startIconButtonGeneral={<CancelIcon color="secondary" />}
                onClickButtonGeneral={() => clearFilters()}
                enabledButtonSecondary={status === CaseClosureEnum.ERROR}
                titleButtonSecondary={'Eliminar Casos ERROR'}
                startIconButtonSecondary={<DeleteIcon color="error" />}
                onClickButtonSecondary={handleDeleteErrorCases}
                loadingButtonSecondary={deleting}
                columns={columns}
                rows={rows}
                rowCount={lengthRows}
                disableRow={true}
                loading={loading}
                onSetPage={(paginationModel: any) => paginator(paginationModel)}
                filterAvanced={true}
                onFilterAvanced={(filter: any) => validateFilters(filter)}
                enabledSearch={true}
                searchField={
                    <Grid
                        container
                        spacing={{ xs: 1.5, md: 1.5 }}
                        columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                    >
                        <Grid item xs={12} md={3}>
                            <DatePickerField
                                label="Fecha inicio"
                                name="startDate"
                                size="small"
                                maxDate={dayjs()}
                                disabled={startDate}
                                value={startDate}
                                onChange={(value) => validateStartDate(value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <DatePickerField
                                label="Fecha fin"
                                name="endDate"
                                size="small"
                                minDate={dayjs(startDate)}
                                maxDate={dayjs()}
                                disabled={!startDate || endDate}
                                value={endDate}
                                onChange={(value) => validateEndDate(value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FieldSelect
                                data={dataStatus}
                                label="Estado"
                                name="status"
                                size="small"
                                value={status}
                                onChange={(value: any) =>
                                    validateStatus(value?.target?.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FieldText
                                label="Caso"
                                name="caseId"
                                size="small"
                                value={caseId}
                                onChange={(value: any) =>
                                    validateCaseId(value?.target?.value)
                                }
                            />
                        </Grid>
                    </Grid>
                }
            />
            {open && (
                <ModalCaseClosure
                    open={open}
                    onClose={(value: boolean) => setOpen(value)}
                    saveLoad={(value: boolean) => setSaveLoad(value)}
                />
            )}
        </>
    );
};

export default ListCaseClosure;
